<style>
.body-content {
    overflow: hidden;
}
</style>
<template>
    <div class="d-flex flex-wrap-none">
        <div class="left-nav-sec data-source-nav" role="navigation" aria-labelledby="DataSourcesHeading">
            <div v-show="!loading">
                <h1 class="admin-content-heading" id="DataSourcesHeading">Data Sources</h1>
                <ul class="nav navbar-nav">
                    <li class="nav-item" :data="datasource.Value" v-for="datasource in sortedDataSourceNames">
                        <!--  :title="datasource.unsavedChanges ? ['Unsaved changes'] : []" :class="datasource.unsavedChanges ? ['icon-edit'] : ['icon-data']" -->
                        <a tabindex="0" class="nav-link" :id="datasource.Key + '_SelectLink'" :class="{ active: activeDatasourceName != null && datasource.Key == activeDatasourceName.Key }" @click.prevent="openDataSource(datasource.Key)"> <span class="icon icon-data"></span>{{ datasource.Value }} </a>
                    </li>
                    <li class="nav-item">
                        <div class="form-group my-2" v-if="canEdit" :class="newTableNameError ? ['has-error'] : []">
                            <label class="sr-only" for="NewTableName">New Data Source Name</label>
                            <div class="input-group input-group-sm mb-1">
                                <input type="text" id="NewTableName" ref="NewTableName" v-model="newTableName" class="form-control" placeholder="New Data Source" @keyup.enter="addTable" />
                                <span class="input-group-append">
                                    <button type="button" class="btn btn-outline-success" @click="addTable" id="NewTableBtn">Add</button>
                                </span>
                            </div>
                            <div class="help-block text-danger" v-show="newTableNameError">{{ newTableNameError }}</div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <div id="dataSourcesFrame" class="admin-content data-source-content" role="main" aria-label="Data Source Content">
            <LoadingWheel v-if="loading" :width="100" />
            <div v-show="!loading">
                <template v-if="activeDatasourceName == null">
                    <p class="admin-content-placeholder mt-0">Select a data source.</p>
                </template>
                <div v-else>
                    <DataSource :key="activeDatasourceName.Key" :name="activeDatasourceName.Key" :displayName="activeDatasourceName.Value" @saved="savedHandler" @renamed="renamedHandler($event as string)" @delete="deleteTable" @unsaved-changes="activeUnsavedChanges = $event as boolean" :newlyCreated="activeIsNew" :canTestEdit="canTestEdit" :canNonTestEdit="canEdit" :canTestView="canTestView" :canNonTestView="canView" :canEditProtected="canEditProtected" />
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, watch, onUnmounted, /*defineProps, defineEmits,*/ inject, SetupContext } from "vue";
import { useMainStore } from "@/mainStore";
//import NonIndexStyles from '@/lib/nonIndexStyles.3.vue'
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import DataSource from "./DataSource.3.vue";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import { showModalInjectKey, showModal } from "@/AppShell.3.vue.InjectKeys";
import DatasourceReferencesModal from "./ReferencesModal.3.vue";
import getData from "../../lib/getData";
import { asyncComputed } from "@vueuse/core";
import { DatasourceModificationResult } from "@cs/DatasourceModificationResult";
interface Props {
    settings: { id: string } | null; // TODO
}
export default defineComponent({
    components: {
        DataSource,
        LoadingWheel,
    },
    props: ["settings"],
    setup(props: Props, ctx: SetupContext) {
        const showModal = inject(showModalInjectKey) as showModal;

        //defineProps<Props>();
        const loading = ref<boolean>(true);
        const newTableName = ref<string | null>(null);
        const newTableNameError = ref<string | null>(null);
        const canEdit = ref<boolean>(true);
        const canView = ref<boolean>(true);
        const canTestEdit = ref<boolean>(true);
        const canTestView = ref<boolean>(true);
        const canEditProtected = ref<boolean>(true);
        const datasourceNames = ref<{ Key: string; Value: string }[]>([]);
        const activeDatasourceName = ref<{ Key: string; Value: string } | null>(null);
        const activeIsNew = ref<boolean>(false);
        const activeUnsavedChanges = ref<boolean>(false);

        const mainStore = useMainStore();

        const emit = ctx.emit;

        //sortedDataSources() {
        //    return datasources.value.slice().sort((a, b) => (a.name || "").localeCompare(b.name));
        //},
        const sortedDataSourceNames = computed(() => {
            return datasourceNames.value.slice().sort((a, b) => (a.Value || "").localeCompare(b.Value));
        });

        const adminUrl = asyncComputed(async () => {
            return mainStore.getUrl("admin");
        });

        const onBeforeUnload = (event: Event) => {
            if (activeUnsavedChanges.value) {
                event.preventDefault();
                //@ts-expect-error
                event.returnValue = "You have unsaved changes";
            }
        };

        window.addEventListener("beforeunload", onBeforeUnload);
        onUnmounted(() => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        });

        const addTable = () => {
            if (newTableName.value == null || /^\s*$/.test(newTableName.value)) {
                newTableNameError.value = "Must not be blank";
            } else if (datasourceNames.value.some(i => i.Key == newTableName.value)) {
                newTableNameError.value = "Must be unique";
            } else {
                //TODO - datasources.value.push({ name: newTableName.value, count: 0, keys: [null], data: [[null]], open: true, unsavedChanges: true });
                //TODO - openDataSource.value(datasources[this.value.datasources.length - 1]); // open on creation
                datasourceNames.value.push({ Key: newTableName.value, Value: newTableName.value });
                openDataSource(newTableName.value, true, true); // open on creation
                newTableName.value = null;
                newTableNameError.value = null;
            }
        };
        const openDataSource = (datasourceName: string, skipHistory?: boolean, isNew?: boolean) => {
            if (activeUnsavedChanges.value && !confirm("You have unsaved changes. Continuing will lose them.")) {
                return;
            }
            activeDatasourceName.value = datasourceNames.value.find(d => d.Key == datasourceName)!;
            activeIsNew.value = isNew ?? false;
            activeUnsavedChanges.value = false;

            if (!skipHistory) {
                emit("update-page", { id: activeDatasourceName.value?.Key }, loading.value);
            }
        };
        const savedHandler = () => {
            if (activeIsNew.value) {
                emit("update-page", { id: activeDatasourceName.value!.Key }, loading.value);
                activeIsNew.value = false;
            }
            if (!datasourceNames.value.find(d => d == activeDatasourceName.value)) {
                datasourceNames.value.push(activeDatasourceName.value!);
            }
        };
        const renamedHandler = (newDisplayName: string) => {
            activeDatasourceName.value!.Value = newDisplayName;
        };
        const deleteTable = async (/*statusUpdate*/) => {
            if (!confirm("Delete data source?")) {
                return;
            }
            //statusUpdate("in-progress");
            var deleteUrl = await mainStore.getUrl("adminDataSourceDelete");

            //try {
            const formData = new FormData();
            formData.append("name", activeDatasourceName.value!.Key);
            const deleteResponse = await fetch(deleteUrl, {
                method: "POST",
                headers: await getXsftHeader(mainStore),
                credentials: "same-origin",
                body: formData,
            });
            throwIfNonSuccessStatus(deleteResponse);
            const deleteResponseJson = (await deleteResponse.json()) as DatasourceModificationResult;
            if (deleteResponseJson.success) {
                datasourceNames.value = datasourceNames.value.filter(i => i != activeDatasourceName.value);
                //statusUpdate("ok"); // pointless...?
                //if (datasourceNames.value.length > 0) {
                // clear page on deletion
                activeDatasourceName.value = null;
                //}
                //} catch (e) {
                //    statusUpdate("fail");
                //    throw e;
                //}
            } else {
                showModal(DatasourceReferencesModal, {
                    result: deleteResponseJson,
                });
            }
        };

        watch(
            () => props.settings?.id,
            () => {
                if (props.settings?.id == null || props.settings.id == activeDatasourceName.value?.Key) {
                    return;
                }
                openDataSource(props.settings.id);
            },
        );

        (async () => {
            const datasourceData = await getData(mainStore, "AdminDataSources", () => mainStore.getUrl("adminDataSourcesData"));
            canEdit.value = datasourceData.CanEdit;
            canView.value = datasourceData.CanView;
            canTestEdit.value = datasourceData.CanTestEdit;
            canTestView.value = datasourceData.CanTestView;
            canEditProtected.value = datasourceData.CanEditProtected;
            datasourceNames.value = datasourceData.TableKeys;
            if (props.settings?.id) {
                openDataSource(props.settings.id, false);
            }
            loading.value = false;
        })();

        return {
            ...props,

            // state
            loading,
            newTableName,
            newTableNameError,
            canEdit,
            canView,
            canTestEdit,
            canTestView,
            canEditProtected,
            datasourceNames,
            activeDatasourceName,
            activeIsNew,
            activeUnsavedChanges,

            //computed
            sortedDataSourceNames,
            adminUrl,

            //methods
            addTable,
            openDataSource,
            savedHandler,
            renamedHandler,
            deleteTable,
        };
    },
});
</script>
